import { API_URL } from "env";
import { CollectVourcherResponse, DeleteCollectedVourcherResponse, MyVourcherListResponse, Vourcher, VourcherDetailResponse, VoucherApiType, VoucherByIdResponseV2, VoucherListResponseV2, VoucherQuoteResponse, VoucherReserveResponse, VoucherRule, VoucherTemplateItemV2, VoucherValidateResponse } from "./VourcherType";
import { toast } from "react-toastify";

interface VourcherListParams {
    sort?: string[];
    name?: string;
    code?: string;
    type?: VoucherApiType;
    limit?: number;
    page?: number;
}

export interface VoucherItem {
    id: number;
    name: string;
    avatar: string | null;
    code: string;
    describe: string;
    is_active: number;
    type: number;
    value: number;
    max_discount: number | null;
    min_amount: number;
    position: number;
    type_customer: number;
    type_voucher: number | null;
    start_date: string;
    exp_date: string;
    max_use_voucher: number;
    max_use_voucher_day: number;
    used_voucher: number;
    customer_usage_limit: number | null;
    created_at: string;
    updated_at: string;
    end_date?: string | null;
    cap_amount?: number | null;
    global_quota?: number | null;
}

export interface VoucherByIdResponse {
    status: boolean;
    data: VoucherItem | null;
    msg: string | null;
}

export interface GetVourcherDetailResponse {
    status: boolean;
    data: VoucherTemplateItemV2 | null;
    msg?: string | null;
}

export interface UsedVourcherHistoryItem {
    id: number;
    user_id: number;
    voucher_template_id: number;
    status: string;
    expires_at: string;
    used_order_id: number;
    created_at: string;
    updated_at: string;
    template: {
        id: number;
        name: string;
        code: string;
        type: string;
        value: string;
        cap_amount: string;
        global_quota: number;
        start_date: string;
        end_date: string;
        created_at: string;
        updated_at: string;
    };
    order: {
        id: number;
        code: string;
        status: number;
        total: number;
        actual_total: number | null;
        created_at: string;
    };
}
function parseVoucherNumber(value: number | string | null | undefined): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

function parseVoucherNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeRuleValue(ruleValue: VoucherRule['rule_value']): VoucherRule['rule_value'] {
    if (Array.isArray(ruleValue)) {
        return ruleValue.map((item) => {
            const parsed = Number(item);
            return Number.isFinite(parsed) ? parsed : String(item);
        });
    }

    if (ruleValue === null || ruleValue === undefined || ruleValue === '') return null;

    const parsed = Number(ruleValue);
    return Number.isFinite(parsed) ? parsed : String(ruleValue);
}

function normalizeVoucherRule(rule: VoucherRule): VoucherRule {
    const normalizedProductDetails = Array.isArray(rule.product_details)
        ? rule.product_details.map((product) => ({
            ...product,
            id: parseVoucherNumber(product.id),
            price: parseVoucherNumber(product.price),
            discount_price: parseVoucherNumber(product.discount_price),
            percent: typeof product.percent === 'number' ? product.percent : parseVoucherNumber(product.percent),
        }))
        : [];

    return {
        id: rule.id,
        voucher_id: rule.voucher_id,
        rule_type: rule.rule_type,
        rule_value: normalizeRuleValue(rule.rule_value),
        rule_description: rule.rule_description,
        target_names: Array.isArray(rule.target_names) ? rule.target_names.map(String) : [],
        is_eligible: typeof rule.is_eligible === 'boolean' ? rule.is_eligible : null,
        product_details: normalizedProductDetails,
        cart_matched_ids: Array.isArray(rule.cart_matched_ids) ? rule.cart_matched_ids : [],
    };
}

function normalizeVoucherDetail(voucher: VoucherTemplateItemV2): VoucherTemplateItemV2 {
    const normalizedGlobalQuota = parseVoucherNullableNumber(voucher.global_quota);
    const cartSummary = voucher.cart_summary;

    return {
        ...voucher,
        value: parseVoucherNumber(voucher.value),
        cap_amount: parseVoucherNullableNumber(voucher.cap_amount),
        global_quota: normalizedGlobalQuota,
        rules: Array.isArray(voucher.rules) ? voucher.rules.map(normalizeVoucherRule) : [],
        cart_eligible: typeof voucher.cart_eligible === 'boolean' ? voucher.cart_eligible : null,
        cart_summary: cartSummary
            ? {
                total_items: parseVoucherNumber(cartSummary.total_items),
                total_amount: parseVoucherNumber(cartSummary.total_amount),
            }
            : null,
    };
}

function mapVoucherTypeToLegacyNumber(type: VoucherApiType): number {
    if (type === 'percent') return 2;
    if (type === 'ship') return 3;
    if (type === 'gift') return 4;
    return 1;
}

function extractMinAmount(voucher: VoucherTemplateItemV2): number {
    const minRule = voucher.rules?.find(rule => rule.rule_type === 'min_order_amount');
    if (!minRule) return 0;
    if (Array.isArray(minRule.rule_value)) {
        return parseVoucherNumber(minRule.rule_value[0]);
    }
    return parseVoucherNumber(minRule.rule_value);
}

function extractDescription(voucher: VoucherTemplateItemV2): string {
    const firstRuleDesc = voucher.rules?.find(rule => !!rule.rule_description)?.rule_description;
    if (firstRuleDesc) return firstRuleDesc;

    if (voucher.type === 'percent') {
        return `Giảm ${parseVoucherNumber(voucher.value)}%`;
    }

    if (voucher.type === 'ship') {
        return 'Ưu đãi phí vận chuyển';
    }

    if (voucher.type === 'gift') {
        return 'Ưu đãi quà tặng';
    }

    return `Giảm ${parseVoucherNumber(voucher.value).toLocaleString('vi-VN')}đ`;
}

function normalizeVoucherV2ToVoucherItem(voucher: VoucherTemplateItemV2): VoucherItem {
    const normalizedValue = parseVoucherNumber(voucher.value);
    const normalizedCapAmount = voucher.cap_amount === null ? null : parseVoucherNumber(voucher.cap_amount);

    return {
        id: voucher.id,
        name: voucher.name,
        avatar: null,
        code: voucher.code,
        describe: extractDescription(voucher),
        is_active: 1,
        type: mapVoucherTypeToLegacyNumber(voucher.type),
        value: normalizedValue,
        max_discount: normalizedCapAmount,
        min_amount: extractMinAmount(voucher),
        position: 0,
        type_customer: 0,
        type_voucher: null,
        start_date: voucher.start_date || '',
        exp_date: voucher.end_date || '',
        max_use_voucher: voucher.global_quota ?? 0,
        max_use_voucher_day: 0,
        used_voucher: 0,
        customer_usage_limit: null,
        created_at: '',
        updated_at: '',
        end_date: voucher.end_date,
        cap_amount: normalizedCapAmount,
        global_quota: voucher.global_quota,
    };
}

function normalizeVoucherItemToVourcher(item: VoucherItem): Vourcher {
    return {
        ...item,
        type_customer: String(item.type_customer),
        max_use_voucher: item.max_use_voucher,
        max_use_voucher_day: item.max_use_voucher_day,
        customer_usage_limit: item.customer_usage_limit ?? 0,
        created_at: item.created_at,
        updated_at: item.updated_at,
        start_date: item.start_date,
        exp_date: item.exp_date,
        end_date: item.end_date,
        cap_amount: item.cap_amount,
        global_quota: item.global_quota,
        rules: [],
    };
}

async function fetchVourcherList(params?: VourcherListParams): Promise<VourcherDetailResponse> {
    const query = new URLSearchParams();
    if (params) {
        if (params.sort) params.sort.forEach(s => query.append('sort[]', s));
        if (params.name) query.append('name', params.name);
        if (params.code) query.append('code', params.code);
        if (params.type) query.append('type', params.type);
        if (params.limit) query.append('limit', String(params.limit));
        if (params.page) query.append('page', String(params.page));
    }

    const url = `${API_URL}/api/v1/fe/vouchers_v2${query.toString() ? `?${query.toString()}` : ''}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json() as VoucherListResponseV2;
        const items = (data.data?.items || [])
            .map(normalizeVoucherDetail)
            .map(normalizeVoucherV2ToVoucherItem)
            .map(normalizeVoucherItemToVourcher);

        return {
            status: data.status,
            msg: data.msg || null,
            data: {
                total: data.data?.total || 0,
                current_page: data.data?.current_page || params?.page || 1,
                limit: data.data?.limit,
                last_page: data.data?.last_page,
                items,
            },
        };
    } catch (error) {
        console.error('Failed to fetch vourcher list:', error);
        return {
            data: {
                total: 0,
                current_page: 1,
                limit: params?.limit,
                last_page: 1,
                items: [],
            },
            status: false,
            msg: 'Failed to fetch vourcher list',
        };
    }

}
async function checkVourcherListProducts(vourcherCode: string, total: number, listProduct: { id: number, product_category_id: number, price: number }[], customer_id: number) {
    try {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No token found');
        }
        const formData = new FormData();
        formData.append('voucher_code', vourcherCode);
        formData.append('total', total.toString());
        formData.append('customer_id', customer_id.toString());
        formData.append('list_products', JSON.stringify(listProduct));
        const response = await fetch(`${API_URL}/api/v1/fe/order/check_voucher_code`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Failed to fetch vourcher list:', error);
        toast.error('Kiểm tra voucher thất bại. Vui lòng thử lại.', {
            position: "bottom-right",
        });
    }
}

interface VoucherQuoteItem {
    product_id: number;
    quantity: number;
}

async function validateVoucher(code: string, items: VoucherQuoteItem[]): Promise<VoucherValidateResponse> {
    const token = localStorage.getItem('token') || '';

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/voucher/validate`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                code,
                items,
            }),
        });

        const data = await response.json();
        return data as VoucherValidateResponse;
    } catch (error) {
        console.error('Failed to validate voucher:', error);
        return {
            success: false,
            message: 'Failed to validate voucher',
        };
    }
}

async function quoteVouchers(codes: string[], shippingFee: number, items: VoucherQuoteItem[], gift_ids?: number[]): Promise<VoucherQuoteResponse> {
    const token = localStorage.getItem('token') || '';

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/voucher/quote`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                codes,
                shipping_fee: shippingFee,
                items,
                gift_ids,
            }),
        });

        const data = await response.json();
        return data as VoucherQuoteResponse;
    } catch (error) {
        console.error('Failed to quote vouchers:', error);
        return {
            success: false,
            message: 'Failed to quote vouchers',
        };
    }
}

async function reserveVouchers(codes: string[], shippingFee: number, items: VoucherQuoteItem[]): Promise<VoucherReserveResponse> {
    const token = localStorage.getItem('token') || '';

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/voucher/reserve`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                codes,
                shipping_fee: shippingFee,
                items,
            }),
        });

        const data = await response.json();
        return data as VoucherReserveResponse;
    } catch (error) {
        console.error('Failed to reserve vouchers:', error);
        return {
            success: false,
            message: 'Failed to reserve vouchers',
        };
    }
}

async function fetchVoucherDetail(id: number | string): Promise<VoucherByIdResponse> {
    try {
        const data = await getVourcherDetail(id);
        return {
            status: data.status,
            data: data.data ? normalizeVoucherV2ToVoucherItem(data.data) : null,
            msg: data.msg || null,
        };
    } catch (error) {
        console.error(`Failed to fetch voucher detail id ${id}:`, error);
        return {
            status: false,
            data: null,
            msg: 'Failed to fetch voucher detail',
        };
    }
}

async function getVourcherDetail(id: number | string): Promise<GetVourcherDetailResponse> {
    const url = `${API_URL}/api/v1/fe/voucher_v2/${id}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json() as VoucherByIdResponseV2;
        return {
            status: data.status,
            data: data.data ? normalizeVoucherDetail(data.data) : null,
            msg: data.msg || null,
        };
    } catch (error) {
        console.error(`Failed to fetch vourcher detail v2 id ${id}:`, error);
        return {
            status: false,
            data: null,
            msg: 'Failed to fetch vourcher detail',
        };
    }
}

async function fetchMyVourcherList(): Promise<MyVourcherListResponse> {
    const token = localStorage.getItem('token') || '';

    if (!token) {
        return {
            status: false,
            data: {
                total: 0,
                items: [],
            },
            msg: 'Unauthorized',
        };
    }

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/vouchers_v2/my`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();
        return data as MyVourcherListResponse;
    } catch (error) {
        console.error('Failed to fetch my vourcher list:', error);
        return {
            status: false,
            data: {
                total: 0,
                items: [],
            },
            msg: 'Failed to fetch my vourcher list',
        };
    }
}

async function saveVourcher(id: number | string): Promise<CollectVourcherResponse> {
    const token = localStorage.getItem('token') || '';

    if (!token) {
        return {
            status: false,
            msg: 'Unauthorized',
        };
    }

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/voucher_v2/${id}/collect`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
        });

        const data = await response.json();
        return data as CollectVourcherResponse;
    } catch (error) {
        console.error(`Failed to save vourcher id ${id}:`, error);
        return {
            status: false,
            msg: 'Failed to save vourcher',
        };
    }
}

async function deleteSavedVourcher(id: number | string): Promise<DeleteCollectedVourcherResponse> {
    const token = localStorage.getItem('token') || '';

    if (!token) {
        return {
            status: false,
            msg: 'Unauthorized',
        };
    }

    try {
        const response = await fetch(`${API_URL}/api/v1/fe/voucher_v2/${id}/collect`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();
        return data as DeleteCollectedVourcherResponse;
    } catch (error) {
        console.error(`Failed to delete saved vourcher id ${id}:`, error);
        return {
            status: false,
            msg: 'Failed to delete saved vourcher',
        };
    }
}


async function getUsedVourcherHistory(page: number, startDate?: string, endDate?: string): Promise<{ status: boolean; data: { total: number; items: UsedVourcherHistoryItem[] }; msg: string }> {
    const token = localStorage.getItem('token') || '';

    if (!token) {
        window.location.href = '/';
        return {
            status: false,
            data: {
                total: 0,
                items: [],
            },
            msg: 'Unauthorized',
        };
    }

    try {
        const query = new URLSearchParams();
        query.append('page', String(page));
        if (startDate) query.append('start_date', startDate);
        if (endDate) query.append('end_date', endDate);

        const response = await fetch(`${API_URL}/api/v1/fe/customer/used_vouchers?${query.toString()}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        const data = await response.json();
        return data as { status: boolean; data: { total: number; items: UsedVourcherHistoryItem[] }; msg: string };
    } catch (error) {
        console.error('Failed to fetch used vourcher history:', error);
        return {
            status: false,
            data: {
                total: 0,
                items: [],
            },
            msg: 'Failed to fetch used vourcher history',
        };
    }
}

export {
    fetchVourcherList,
    fetchMyVourcherList,
    checkVourcherListProducts,
    saveVourcher,
    deleteSavedVourcher,
    getVourcherDetail,
    fetchVoucherDetail,
    validateVoucher,
    quoteVouchers,
    reserveVouchers,
    getUsedVourcherHistory,
}